/**
 * One-time welcome after first successful license redeem.
 * Locks the UI until the binary Hello World CTA is pressed.
 */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

/** "Hello World" in ASCII binary (bytes separated by spaces). */
export const HELLO_WORLD_BINARY =
  '01001000 01100101 01101100 01101100 01101111 00100000 01010111 01101111 01110010 01101100 01100100';

export const firstWelcomePendingKey = (userId) => `skylite-first-welcome-pending-${userId}`;
export const firstWelcomeSeenKey = (userId) => `skylite-first-welcome-seen-${userId}`;

export function markFirstWelcomePending(userId) {
  if (!userId) return;
  try {
    localStorage.setItem(firstWelcomePendingKey(userId), '1');
  } catch (_) {
    /* ignore */
  }
}

export function clearFirstWelcomePending(userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(firstWelcomePendingKey(userId));
  } catch (_) {
    /* ignore */
  }
}

export function markFirstWelcomeSeen(userId) {
  if (!userId) return;
  try {
    localStorage.setItem(firstWelcomeSeenKey(userId), '1');
    localStorage.removeItem(firstWelcomePendingKey(userId));
  } catch (_) {
    /* ignore */
  }
}

export function shouldShowFirstWelcome(userId) {
  if (!userId) return false;
  try {
    if (localStorage.getItem(firstWelcomeSeenKey(userId)) === '1') return false;
    return localStorage.getItem(firstWelcomePendingKey(userId)) === '1';
  } catch (_) {
    return false;
  }
}

export default function FirstWelcomeOverlay({ onContinue }) {
  const [ctaRevealed, setCtaRevealed] = useState(false);

  return (
    <Box
      className="notranslate"
      translate="no"
      dir="ltr"
      lang="en"
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-welcome-title"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 51000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        background: 'linear-gradient(145deg, rgba(6, 12, 28, 0.97) 0%, rgba(12, 20, 40, 0.98) 100%)',
        backdropFilter: 'blur(6px)'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 620,
          width: '100%',
          maxHeight: 'min(92vh, 720px)',
          overflow: 'auto',
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          border: '1px solid rgba(160, 220, 255, 0.55)',
          background: 'linear-gradient(160deg, rgba(18, 28, 52, 0.98), rgba(10, 16, 32, 0.99))',
          boxShadow:
            '0 0 0 1px rgba(130, 210, 255, 0.22), 0 0 0 3px rgba(80, 150, 220, 0.12), 0 18px 48px rgba(0, 0, 0, 0.55)'
        }}
      >
        <Typography
          id="first-welcome-title"
          variant="h5"
          sx={{
            fontWeight: 800,
            color: '#e8f6ff',
            mb: 2.5,
            letterSpacing: '0.02em',
            lineHeight: 1.3
          }}
        >
          Welcome to CommandLine Academy
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: 'rgba(220, 240, 255, 0.92)', mb: 2.25, lineHeight: 1.75, fontSize: '1.02rem' }}
        >
          The future of IT, software, cybersecurity, AI, and all other computer-related technologies
          begins here. CommandLine is your training ground — grow and excel as you become one of the
          people who shape that future.
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: 'rgba(220, 240, 255, 0.9)', mb: 2.75, lineHeight: 1.75, fontSize: '1.02rem' }}
        >
          From here, you’ll build real skills: Linux, Python, SQL, security, and the habits that IT
          and software careers are built on. Take it one level at a time. Practice beats perfection.
        </Typography>

        <Typography
          component="blockquote"
          sx={{
            m: 0,
            mb: 2.5,
            pl: 2,
            borderLeft: '3px solid rgba(125, 207, 255, 0.55)',
            color: '#c8ecff',
            fontStyle: 'italic',
            lineHeight: 1.7,
            fontSize: '1.05rem'
          }}
        >
          Anyone who was great at anything had a Day 1. Welcome to yours.
        </Typography>

        <Box sx={{ mb: 3.5, textAlign: 'right' }}>
          <Typography
            sx={{
              color: '#e8f6ff',
              fontWeight: 700,
              fontFamily: '"Segoe Script", "Brush Script MT", "Apple Chancery", cursive',
              fontSize: '1.35rem',
              lineHeight: 1.2,
              letterSpacing: '0.02em'
            }}
          >
            James Houston II
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(180, 215, 240, 0.85)', display: 'block', mt: 0.35 }}>
            CEO, Enterceptor Corporation
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => onContinue?.()}
            onMouseEnter={() => setCtaRevealed(true)}
            onMouseLeave={() => setCtaRevealed(false)}
            onFocus={() => setCtaRevealed(true)}
            onBlur={() => setCtaRevealed(false)}
            aria-label="Hello World — continue"
            title="Hello World"
            sx={{
              px: 2.5,
              py: 1.15,
              minWidth: { xs: '100%', sm: 280 },
              maxWidth: '100%',
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: 'Consolas, "Courier New", monospace',
              fontSize: ctaRevealed
                ? { xs: '0.95rem', sm: '1.05rem' }
                : { xs: '0.55rem', sm: '0.65rem' },
              letterSpacing: ctaRevealed ? '0.06em' : { xs: '0.01em', sm: '0.03em' },
              lineHeight: 1.4,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              color: '#e8f6ff',
              border: '1px solid rgba(130, 210, 255, 0.38)',
              borderRadius: '10px',
              background:
                'linear-gradient(160deg, rgba(70, 140, 220, 0.28), rgba(30, 70, 140, 0.22))',
              boxShadow:
                'inset 0 1px 0 rgba(180, 230, 255, 0.18), 0 0 0 1px rgba(130, 210, 255, 0.1), 0 8px 22px rgba(0, 20, 50, 0.35)',
              backdropFilter: 'blur(8px)',
              transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, font-size 0.15s ease',
              '&:hover': {
                border: '1px solid rgba(160, 220, 255, 0.55)',
                background:
                  'linear-gradient(160deg, rgba(90, 160, 235, 0.38), rgba(40, 90, 170, 0.32))',
                boxShadow:
                  'inset 0 1px 0 rgba(200, 240, 255, 0.22), 0 0 0 1px rgba(150, 220, 255, 0.16), 0 10px 28px rgba(30, 90, 180, 0.28)'
              }
            }}
          >
            {ctaRevealed ? 'Hello World' : HELLO_WORLD_BINARY}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
